import { useEffect, useRef } from 'react'

export type CanvasGestureCallbacks = {
  /** Single-finger pan delta in pixels (and two-finger pan center motion). */
  onPan?: (dxPx: number, dyPx: number) => void
  /**
   * Pinch with anchor point in pixels relative to the viewport, and a scale
   * factor where >1 means zoom in.
   */
  onPinch?: (anchorXPx: number, anchorYPx: number, scaleFactor: number) => void
  /** Double-tap to zoom-in/out toggle at the tap location. */
  onDoubleTap?: (anchorXPx: number, anchorYPx: number) => void
}

export type CanvasGestureOptions = {
  /** Allow a single finger to pan the surface. Default: true. */
  enableSingleFingerPan?: boolean
  /** Decay multiplier per frame for fling momentum, 0..1. Default: 0.92. */
  momentumDecay?: number
  /** Maximum frames the momentum loop runs before snapping to a stop. */
  maxMomentumFrames?: number
  /** Disable the gestures entirely (e.g. while a drag-and-drop is in flight). */
  disabled?: boolean
}

type Vec = { x: number; y: number }

const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_DIST = 28

/**
 * Touch-driven canvas gestures: single-finger pan with momentum-on-release,
 * two-finger pan, two-finger pinch-zoom, and double-tap zoom. Designed for
 * the three canvas surfaces (dashboard horizontal, vertical timeline, drawing
 * board) — each one supplies the callbacks it cares about.
 */
export const useCanvasGestures = (
  targetRef: React.RefObject<HTMLElement | null>,
  callbacks: CanvasGestureCallbacks,
  options: CanvasGestureOptions = {},
) => {
  const callbacksRef = useRef(callbacks)
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  const optsRef = useRef(options)
  useEffect(() => {
    optsRef.current = options
  }, [options])

  useEffect(() => {
    const element = targetRef.current
    if (!element) return undefined

    const activePoints = new Map<number, Vec>()
    let lastCenter: Vec | null = null
    let lastDistance: number | null = null
    let lastSingleFingerPos: Vec | null = null
    const velocitySamples: Array<{ x: number; y: number; t: number }> = []
    let momentumRaf = 0
    let lastTapTime = 0
    let lastTapPos: Vec | null = null

    const stopMomentum = () => {
      if (momentumRaf) cancelAnimationFrame(momentumRaf)
      momentumRaf = 0
    }

    const startMomentum = () => {
      if (velocitySamples.length < 2) return
      const now = performance.now()
      const recent = velocitySamples.filter((sample) => now - sample.t < 100)
      if (recent.length < 2) return
      const first = recent[0]
      const last = recent[recent.length - 1]
      const dt = Math.max(1, last.t - first.t)
      let vx = ((last.x - first.x) / dt) * 16 // px per ~frame
      let vy = ((last.y - first.y) / dt) * 16
      const decay = optsRef.current.momentumDecay ?? 0.92
      const maxFrames = optsRef.current.maxMomentumFrames ?? 120
      let frames = 0

      const tick = () => {
        if (frames++ > maxFrames || (Math.abs(vx) < 0.2 && Math.abs(vy) < 0.2)) {
          momentumRaf = 0
          return
        }
        callbacksRef.current.onPan?.(vx, vy)
        vx *= decay
        vy *= decay
        momentumRaf = requestAnimationFrame(tick)
      }
      momentumRaf = requestAnimationFrame(tick)
    }

    const getRelative = (point: Vec): Vec => {
      const bounds = element.getBoundingClientRect()
      return { x: point.x - bounds.left, y: point.y - bounds.top }
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (optsRef.current.disabled) return
      if (event.pointerType !== 'touch') return
      stopMomentum()
      activePoints.set(event.pointerId, { x: event.clientX, y: event.clientY })
      velocitySamples.length = 0
      velocitySamples.push({ x: event.clientX, y: event.clientY, t: performance.now() })
      if (activePoints.size === 1) {
        lastSingleFingerPos = { x: event.clientX, y: event.clientY }
        lastDistance = null
        lastCenter = null
      } else if (activePoints.size === 2) {
        const points = Array.from(activePoints.values())
        lastDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
        lastCenter = {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        }
        lastSingleFingerPos = null
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (optsRef.current.disabled) return
      if (event.pointerType !== 'touch') return
      if (!activePoints.has(event.pointerId)) return

      activePoints.set(event.pointerId, { x: event.clientX, y: event.clientY })

      if (activePoints.size === 1 && optsRef.current.enableSingleFingerPan !== false) {
        if (!lastSingleFingerPos) {
          lastSingleFingerPos = { x: event.clientX, y: event.clientY }
          return
        }
        const dx = event.clientX - lastSingleFingerPos.x
        const dy = event.clientY - lastSingleFingerPos.y
        lastSingleFingerPos = { x: event.clientX, y: event.clientY }
        if (dx !== 0 || dy !== 0) {
          callbacksRef.current.onPan?.(dx, dy)
          velocitySamples.push({ x: event.clientX, y: event.clientY, t: performance.now() })
          while (velocitySamples.length > 6) velocitySamples.shift()
        }
        event.preventDefault()
        return
      }

      if (activePoints.size === 2) {
        const points = Array.from(activePoints.values())
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
        const center = {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        }

        if (lastDistance && Math.abs(distance - lastDistance) > 0.5) {
          const anchor = getRelative(center)
          const factor = distance / Math.max(1, lastDistance)
          callbacksRef.current.onPinch?.(anchor.x, anchor.y, factor)
        }

        if (lastCenter) {
          const dx = center.x - lastCenter.x
          const dy = center.y - lastCenter.y
          if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
            callbacksRef.current.onPan?.(dx, dy)
          }
        }

        lastDistance = distance
        lastCenter = center
        event.preventDefault()
      }
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') return
      const released = activePoints.get(event.pointerId)
      activePoints.delete(event.pointerId)

      if (activePoints.size === 0) {
        // Detect double-tap before kicking off fling momentum: very short
        // taps shouldn't fling, and a real fling has measurable velocity
        // anyway.
        if (released && callbacksRef.current.onDoubleTap) {
          const now = performance.now()
          if (
            lastTapPos &&
            now - lastTapTime < DOUBLE_TAP_MS &&
            Math.hypot(released.x - lastTapPos.x, released.y - lastTapPos.y) < DOUBLE_TAP_DIST
          ) {
            const anchor = getRelative(released)
            callbacksRef.current.onDoubleTap(anchor.x, anchor.y)
            lastTapTime = 0
            lastTapPos = null
            velocitySamples.length = 0
            lastSingleFingerPos = null
            return
          }
          lastTapTime = now
          lastTapPos = released
        }
        startMomentum()
        lastSingleFingerPos = null
        lastDistance = null
        lastCenter = null
      } else if (activePoints.size === 1) {
        const remaining = Array.from(activePoints.values())[0]
        lastSingleFingerPos = remaining
        velocitySamples.length = 0
        velocitySamples.push({ x: remaining.x, y: remaining.y, t: performance.now() })
        lastDistance = null
        lastCenter = null
      }
    }

    element.addEventListener('pointerdown', handlePointerDown, { passive: true })
    element.addEventListener('pointermove', handlePointerMove, { passive: false })
    element.addEventListener('pointerup', handlePointerUp, { passive: true })
    element.addEventListener('pointercancel', handlePointerUp, { passive: true })

    return () => {
      stopMomentum()
      element.removeEventListener('pointerdown', handlePointerDown)
      element.removeEventListener('pointermove', handlePointerMove)
      element.removeEventListener('pointerup', handlePointerUp)
      element.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [targetRef])
}
